import { portalText } from "../i18n/portal.js";
/**
 * Every Phosphor glyph the console draws, named once.
 *
 * TWO JOBS.
 *
 * 1. **It is the acceptance assertion for P10-01's tsconfig override.** The imports below
 *    are concrete named members of `@phosphor-icons/react`. Under the inherited
 *    `moduleResolution: "nodenext"` every one of them fails with
 *    `TS2305: Module '@phosphor-icons/react' has no exported member 'Gauge'`, because the
 *    package re-exports its ~1,500 glyphs with extensionless relative specifiers. This file
 *    compiling at all is the proof that `tsconfig.json`'s `bundler` override is in place.
 *    Do not "fix" a resolution error here by reaching into `@phosphor-icons/react/dist/csr/*`.
 *
 * 2. **It resolves the catalogue's icon strings.** `agent_template.icon` is data
 *    (`"ph-duotone ph-moped"`), seeded by a migration and served by `GET /v1/templates`,
 *    because "adding a template must not change wizard code". A string cannot be rendered,
 *    so it is looked up here — and an unknown one raises rather than substituting a
 *    stand-in, for the same reason a missing configuration value raises.
 */
import {
  AddressBook,
  Archive,
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  ArrowsSplit,
  ArrowUDownLeft,
  Bank,
  Bell,
  BellRinging,
  BookOpenText,
  Broadcast,
  Browser,
  Buildings,
  Calendar,
  CalendarBlank,
  CalendarCheck,
  CaretDown,
  CaretUp,
  CaretRight,
  CaretUpDown,
  ChartLineUp,
  ChatCircleDots,
  ChatText,
  Check,
  CheckCircle,
  CircleDashed,
  ClipboardText,
  ClockCounterClockwise,
  ClockCountdown,
  Copy,
  CreditCard,
  Crown,
  DeviceMobile,
  DeviceTablet,
  DotsThree,
  DownloadSimple,
  Envelope,
  EnvelopeSimple,
  EnvelopeSimpleOpen,
  Eye,
  FileText,
  FileX,
  Fingerprint,
  Fire,
  Flask,
  Funnel,
  Gauge,
  Graph,
  GearSix,
  Gift,
  GitBranch,
  Globe,
  GoogleLogo,
  HardDrives,
  Headphones,
  Headset,
  Hourglass,
  Key,
  Lifebuoy,
  Lightning,
  List as ListBars,
  ListChecks,
  Lock,
  LockKey,
  LockSimple,
  MagnifyingGlass,
  Microphone,
  MicrosoftOutlookLogo,
  Minus,
  Moon,
  Moped,
  PaperPlaneRight,
  Password,
  Pause,
  PencilSimple,
  PhoneDisconnect,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneX,
  Play,
  Plug,
  PlugsConnected,
  Plus,
  PlusCircle,
  Printer,
  Prohibit,
  Question,
  Quotes,
  Receipt,
  ShareNetwork,
  ShieldCheck,
  ShieldWarning,
  SignOut,
  SlidersHorizontal,
  Sparkle,
  SquaresFour,
  Stack,
  StopCircle,
  Storefront,
  Sun,
  ThumbsDown,
  ThumbsUp,
  Trash,
  TrendUp,
  UploadSimple,
  Warning,
  WarningCircle,
  WarningOctagon,
  Waveform,
  WhatsappLogo,
  X,
  XCircle,
  Info,
  PlayCircle,
  ArrowBendUpLeft,
  Clock,
  Shapes,
  Phone,
  ChatCircleText,
  Code,
  Stop,
  // The BI configuration screens' data source and relationship glyphs (2026-10-21), named here so the screens ask GLYPH.
  Database,
  Link,
} from "@phosphor-icons/react";
// Six glyphs carry a banned bare term in the DEPENDENCY'S OWN name. They are imported on
// their own lines so each suppression names exactly one term, and they are translated to a
// WeTalk token in GLYPH below — the same anti-corruption shape as a vendor adapter. This file is
// that boundary: a screen asks for `GLYPH.people`, never imports `Users` itself (register 0z.8).
// glossary-lint-disable-next-line call -- Phosphor's glyph name; translated to GLYPH.phoneCall
import { PhoneCall } from "@phosphor-icons/react";
// glossary-lint-disable-next-line user -- Phosphor's glyph name; translated to GLYPH.usersThree
import { UsersThree } from "@phosphor-icons/react";
// glossary-lint-disable-next-line user -- Phosphor's glyph name; translated to GLYPH.people
import { Users as PeopleGlyph } from "@phosphor-icons/react";
// glossary-lint-disable-next-line user -- Phosphor's glyph name; translated to GLYPH.personSwitch
import { UserSwitch as PersonSwitchGlyph } from "@phosphor-icons/react";
// glossary-lint-disable-next-line user -- Phosphor's glyph name; translated to GLYPH.personPlus
import { UserPlus as PersonPlusGlyph } from "@phosphor-icons/react";
// glossary-lint-disable-next-line user -- Phosphor's glyph name; translated to GLYPH.personCircle
import { UserCircle as PersonCircleGlyph } from "@phosphor-icons/react";
// glossary-lint-disable-next-line flag -- Phosphor's glyph name; translated to GLYPH.finishLine
import { FlagCheckered as FinishLineGlyph } from "@phosphor-icons/react";
// glossary-lint-disable-next-line list -- Phosphor's glyph name; translated to GLYPH.numberedSteps
import { ListNumbers as NumberedStepsGlyph } from "@phosphor-icons/react";
// Design sync 2026-10-09 (unit A, the Agent screen): the grouped tab strip, the phone card and the
// Versions tab's publish and restore marks.
import {
  ArrowCounterClockwise,
  ChatsCircle,
  Megaphone,
  Plugs,
  RocketLaunch,
} from "@phosphor-icons/react";
import type { PhosphorIcon } from "@wetalk/ui";

export const GLYPH = {
  phone: Phone,
  chatCircleText: ChatCircleText,
  code: Code,
  stop: Stop,
  info: Info,
  playCircle: PlayCircle,
  arrowBendUpLeft: ArrowBendUpLeft,
  clock: Clock,
  shapes: Shapes,
  finishLine: FinishLineGlyph,
  numberedSteps: NumberedStepsGlyph,
  addressBook: AddressBook,
  archive: Archive,
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  arrowUDownLeft: ArrowUDownLeft,
  arrowsClockwise: ArrowsClockwise,
  // plan-005 OP-38: the Payouts row in the sidebar.
  bank: Bank,
  bell: Bell,
  bellRinging: BellRinging,
  bookOpenText: BookOpenText,
  broadcast: Broadcast,
  // plan-014 A4: the Organization row and the organization picker's "Organization settings".
  buildings: Buildings,
  browser: Browser,
  calendarBlank: CalendarBlank,
  calendarCheck: CalendarCheck,
  caretDown: CaretDown,
  /** numbers-sip-sync: an open number row (the prototype's `ph-caret-up`). */
  caretUp: CaretUp,
  caretRight: CaretRight,
  // plan-014 A4: the organization picker's trigger (the prototype's ph-caret-up-down).
  caretUpDown: CaretUpDown,
  chartLineUp: ChartLineUp,
  chatCircleDots: ChatCircleDots,
  chatText: ChatText,
  check: Check,
  checkCircle: CheckCircle,
  circleDashed: CircleDashed,
  clipboardText: ClipboardText,
  clockCounterClockwise: ClockCounterClockwise,
  clockCountdown: ClockCountdown,
  copy: Copy,
  creditCard: CreditCard,
  deviceTablet: DeviceTablet,
  dotsThree: DotsThree,
  downloadSimple: DownloadSimple,
  envelope: Envelope,
  eye: Eye,
  fileText: FileText,
  // plan-014 A4: the person menu's "My sign-in and devices" (the prototype's ph-fingerprint).
  fingerprint: Fingerprint,
  // Order: the prototype's "Send to the kitchen" (ph-fire).
  fire: Fire,
  flask: Flask,
  funnel: Funnel,
  gauge: Gauge,
  graph: Graph,
  gearSix: GearSix,
  gift: Gift,
  gitBranch: GitBranch,
  headphones: Headphones,
  headset: Headset,
  key: Key,
  lifebuoy: Lifebuoy,
  lightning: Lightning,
  listBars: ListBars,
  listChecks: ListChecks,
  lockSimple: LockSimple,
  magnifyingGlass: MagnifyingGlass,
  // plan-004 WC-51: "Talk in the browser" — the prototype's ph-microphone and ph-stop-circle.
  microphone: Microphone,
  minus: Minus,
  moon: Moon,
  moped: Moped,
  paperPlaneRight: PaperPlaneRight,
  pause: Pause,
  pencilSimple: PencilSimple,
  /** Phosphor's `Users`: two people, the People heading and a contact list of people. */
  people: PeopleGlyph,
  /** Phosphor's `UserCircle`: the person menu's "Account settings" row. */
  personCircle: PersonCircleGlyph,
  /** Phosphor's `UserPlus`: someone joined or was invited. */
  personPlus: PersonPlusGlyph,
  /** Phosphor's `UserSwitch`: an operator acting as a member, or a hand-off to a person. */
  personSwitch: PersonSwitchGlyph,
  // glossary-lint-disable-next-line call -- the vendor glyph's name, kept recognisable at this boundary
  phoneCall: PhoneCall,
  /** Hang up (the prototype's `ph-phone-disconnect`, design sync 2026-10-09). */
  phoneDisconnect: PhoneDisconnect,
  /** numbers-sip-sync: an incoming call in a number's Recent activity (the prototype's `ph-phone-incoming`). */
  phoneIncoming: PhoneIncoming,
  phoneOutgoing: PhoneOutgoing,
  phoneX: PhoneX,
  play: Play,
  plug: Plug,
  plugsConnected: PlugsConnected,
  plus: Plus,
  plusCircle: PlusCircle,
  printer: Printer,
  prohibit: Prohibit,
  question: Question,
  quotes: Quotes,
  receipt: Receipt,
  shareNetwork: ShareNetwork,
  shieldCheck: ShieldCheck,
  shieldWarning: ShieldWarning,
  signOut: SignOut,
  slidersHorizontal: SlidersHorizontal,
  sparkle: Sparkle,
  squaresFour: SquaresFour,
  stopCircle: StopCircle,
  sun: Sun,
  thumbsDown: ThumbsDown,
  thumbsUp: ThumbsUp,
  trash: Trash,
  trendUp: TrendUp,
  uploadSimple: UploadSimple,
  // glossary-lint-disable-next-line user -- the vendor glyph's name, kept recognisable at this boundary
  usersThree: UsersThree,
  warning: Warning,
  warningCircle: WarningCircle,
  waveform: Waveform,
  whatsappLogo: WhatsappLogo,
  x: X,
  xCircle: XCircle,
  // plan-014 SC-P: the prototype's exact icons on the Organization, Finance and My security pages (were substitutes).
  arrowsSplit: ArrowsSplit,
  calendar: Calendar,
  crown: Crown,
  deviceMobile: DeviceMobile,
  envelopeSimple: EnvelopeSimple,
  envelopeSimpleOpen: EnvelopeSimpleOpen,
  fileX: FileX,
  globe: Globe,
  database: Database,
  link: Link,
  googleLogo: GoogleLogo,
  hardDrives: HardDrives,
  hourglass: Hourglass,
  lock: Lock,
  lockKey: LockKey,
  microsoftOutlookLogo: MicrosoftOutlookLogo,
  password: Password,
  stack: Stack,
  storefront: Storefront,
  warningOctagon: WarningOctagon,
  arrowCounterClockwise: ArrowCounterClockwise,
  chatsCircle: ChatsCircle,
  megaphone: Megaphone,
  plugs: Plugs,
  rocketLaunch: RocketLaunch,
} as const satisfies Readonly<Record<string, PhosphorIcon>>;

/**
 * The catalogue's own icon strings, as the seeded rows carry them.
 *
 * Keyed by the Phosphor class name the prototype and the migration both use, so the
 * console renders whatever the row says without a per-template branch.
 */
const BY_CATALOGUE_NAME: Readonly<Record<string, PhosphorIcon>> = {
  "ph-duotone ph-moped": GLYPH.moped,
  "ph-duotone ph-graph": GLYPH.graph,
  "ph-duotone ph-clipboard-text": GLYPH.clipboardText,
  "ph-duotone ph-calendar-check": GLYPH.calendarCheck,
  "ph-duotone ph-lifebuoy": GLYPH.lifebuoy,
  "ph-duotone ph-funnel": GLYPH.funnel,
  "ph-duotone ph-target": GLYPH.funnel,
  "ph-duotone ph-headset": GLYPH.headset,
  "ph-duotone ph-bell-ringing": GLYPH.bellRinging,
  // A finished build's notice (review #73): ready, or failed.
  "ph-duotone ph-check-circle": GLYPH.checkCircle,
  "ph-duotone ph-warning-circle": GLYPH.warningCircle,
  // The operator pause notice (`apps/worker/src/handler/voice-agent-paused/notice.ts`) writes the
  // bare names `pause` and `play`. They were missing, so the first such notice would have thrown
  // UnknownGlyphError inside the notification centre (found 2026-09-29).
  pause: GLYPH.pause,
  play: GLYPH.play,
  // The sandbox gateway's sample notice names `ph-phone-call`; it was missing, so opening the notification centre on
  // the sandbox drew "This page could not load" (found 2026-10-12, ADR 0057's screenshot pass).
  "ph-duotone ph-phone-call": GLYPH.phoneCall,
  // plan-029 WAPP-HIST1: caller protection's notices (API-READ1's `routes/notification/party-notice.ts`) — a number
  // refused often, and an internal agent's access lock. Missing, the first such notice would break the centre.
  "ph-duotone ph-shield-warning": GLYPH.shieldWarning,
  "ph-duotone ph-lock-simple": GLYPH.lockSimple,
};

export class UnknownGlyphError extends Error {
  override readonly name = "UnknownGlyphError";
  constructor(catalogueName: string) {
    super(
      portalText("shell", "the_catalogue_asks_for_the_glyph_value_which_the_console_d_6ca8f4d0", { v0: String(catalogueName) }),
    );
  }
}

/** Resolves a catalogue icon string. Raises on an unknown one — never substitutes. */
export function glyphOf(catalogueName: string): PhosphorIcon {
  const found = BY_CATALOGUE_NAME[catalogueName];
  if (found === undefined) throw new UnknownGlyphError(catalogueName);
  return found;
}
